/**
 * Entity Risk Screening — main user-facing page.
 * Flow: Search → animated source-check → gauge + factor list →
 *       match cards (live + sample) → network graph → STR narrative + PDF.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Search, Loader2, CheckCircle2, AlertTriangle, FileDown,
  AlertCircle, Sparkles, Network, ListChecks, FileText, RefreshCw, Database,
} from "lucide-react";
import { toast } from "sonner";
import RiskGauge from "../components/screening/RiskGauge";
import NetworkGraph from "../components/screening/NetworkGraph";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_BADGE = (status, sample) => {
  if (status === "live") {
    return { fg: "#10b981", bg: "rgba(16,185,129,0.12)", border: "#10b98144", txt: "LIVE" };
  }
  if (sample) {
    return { fg: "#f59e0b", bg: "rgba(245,158,11,0.12)", border: "#f59e0b44", txt: "SAMPLE" };
  }
  return { fg: "#64748b", bg: "rgba(100,116,139,0.12)", border: "#64748b44", txt: "DEMO" };
};

export default function EntityScreeningPage() {
  const [name, setName] = useState("");
  const [pan, setPan] = useState("");
  const [cin, setCin] = useState("");
  const [entityType, setEntityType] = useState("person");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState([]); // animated source-check state
  const [report, setReport] = useState(null);
  const [narrative, setNarrative] = useState("");
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [sourcesMeta, setSourcesMeta] = useState([]);
  const animTimers = React.useRef([]);

  useEffect(() => {
    axios.get(`${API}/entity-screening/sources`, { withCredentials: true })
      .then(r => setSourcesMeta(r.data.sources || []))
      .catch(() => {});
  }, []);

  const cancelAnim = () => {
    animTimers.current.forEach(t => clearTimeout(t));
    animTimers.current = [];
  };

  const animateProgress = (sources) => {
    cancelAnim();
    setProgress([]);
    sources.forEach((s, i) => {
      const t = setTimeout(() => {
        setProgress(prev => [...prev, s]);
      }, 220 * (i + 1));
      animTimers.current.push(t);
    });
  };

  const handleRun = async () => {
    if (!name.trim()) return toast.error("Enter a name");
    setLoading(true); setReport(null); setNarrative(""); setProgress([]);
    // Show the source list animation while the API call is in flight
    const preview = (sourcesMeta || []).map(s => ({
      slug: s.slug, name: s.name,
      data_status: s.data_status, hit: null,
    }));
    preview.unshift({ slug: "opensanctions", name: "OpenSanctions",
                       data_status: "live", hit: null });
    animateProgress(preview);
    try {
      const res = await axios.post(`${API}/entity-screening/run`,
        { name, pan: pan || null, cin: cin || null, entity_type: entityType },
        { withCredentials: true });
      cancelAnim();
      setReport(res.data);
      setProgress(res.data.source_status); // overwrite with real hit/miss
      toast.success(`Risk score: ${res.data.risk.score}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Screening failed");
    } finally { setLoading(false); }
  };

  const onNodeClick = (node) => {
    setName(node.label || "");
    setTimeout(() => handleRun(), 100);
  };

  const generateNarrative = async () => {
    if (!report) return;
    setNarrativeLoading(true);
    try {
      const res = await axios.post(`${API}/entity-screening/str-narrative`,
        { screening_id: report.id }, { withCredentials: true });
      setNarrative(res.data.narrative);
      if (!res.data.used_real_hits) {
        toast.info("No real watch-list hits — generated disclaimer draft");
      }
    } catch (e) {
      toast.error("Narrative generation failed");
    } finally { setNarrativeLoading(false); }
  };

  const downloadPdf = async () => {
    if (!report || !narrative) return toast.error("Generate a narrative first");
    try {
      const res = await axios.post(`${API}/entity-screening/str-pdf`,
        { screening_id: report.id, narrative },
        { withCredentials: true, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `STR_${(report.query.name || "subject").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF download failed");
    }
  };

  return (
    <div data-testid="entity-screening-page" style={{ maxWidth: 1180, margin: "0 auto" }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: "#f1f5f9", margin: 0,
                       display: "inline-flex", alignItems: "center", gap: 10 }}>
          <Sparkles size={22} color="#2563eb" /> Entity Risk Screening
        </h1>
        <p style={{ color: "#64748b", fontSize: 13, marginTop: 6 }}>
          Live OpenSanctions check + Indian compliance sources. Sample-data tables
          are clearly labelled — we never invent a match.
        </p>
      </div>

      {/* Search panel */}
      <div style={{ background: "#0d1117", border: "1px solid #1e2530",
                     borderRadius: 14, padding: 22, marginBottom: 20 }}>
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 md:col-span-5">
            <label style={{ color: "#64748b", fontSize: 10, letterSpacing: 0.6,
                             textTransform: "uppercase" }}>Name *</label>
            <input data-testid="es-name-input" value={name}
                   onChange={e => setName(e.target.value)}
                   onKeyDown={e => e.key === "Enter" && handleRun()}
                   placeholder="Person or company name"
                   style={inputStyle} />
          </div>
          <div className="col-span-6 md:col-span-3">
            <label style={{ color: "#64748b", fontSize: 10, letterSpacing: 0.6,
                             textTransform: "uppercase" }}>PAN (optional)</label>
            <input data-testid="es-pan-input" value={pan}
                   onChange={e => setPan(e.target.value.toUpperCase())}
                   placeholder="ABCDE1234F"
                   maxLength={10} style={inputStyle} />
          </div>
          <div className="col-span-6 md:col-span-3">
            <label style={{ color: "#64748b", fontSize: 10, letterSpacing: 0.6,
                             textTransform: "uppercase" }}>CIN (optional)</label>
            <input data-testid="es-cin-input" value={cin}
                   onChange={e => setCin(e.target.value.toUpperCase())}
                   placeholder="L99999MH2020PLC..."
                   style={inputStyle} />
          </div>
          <div className="col-span-12 md:col-span-1">
            <label style={{ color: "#64748b", fontSize: 10, letterSpacing: 0.6,
                             textTransform: "uppercase" }}>Type</label>
            <select data-testid="es-type-select" value={entityType}
                    onChange={e => setEntityType(e.target.value)}
                    style={{ ...inputStyle, padding: "9px 8px" }}>
              <option value="person">Person</option>
              <option value="company">Company</option>
            </select>
          </div>
        </div>
        <button onClick={handleRun} disabled={loading} data-testid="es-run-btn"
          style={{
            marginTop: 16, padding: "11px 24px", borderRadius: 10,
            background: "#2563eb", color: "#fff", border: "none",
            fontSize: 13, fontWeight: 700, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 8,
            boxShadow: "0 6px 20px rgba(37,99,235,0.35)",
          }}>
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          {loading ? "Screening…" : "Run screening"}
        </button>
      </div>

      {/* Source check animation */}
      {progress.length > 0 && (
        <div data-testid="source-progress" style={{
          background: "#0d1117", border: "1px solid #1e2530",
          borderRadius: 14, padding: 18, marginBottom: 20,
        }}>
          <h3 style={{ color: "#f1f5f9", fontSize: 13, fontWeight: 700, margin: 0,
                         marginBottom: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <ListChecks size={14} /> Sources checked
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {progress.map((p, i) => {
              const b = STATUS_BADGE(p.data_status, p.data_status === "sample");
              const hit = p.hit === true;
              const checking = p.hit === null;
              const Icon = hit ? AlertCircle : (checking ? Loader2 : CheckCircle2);
              return (
                <div key={`${p.slug}-${i}`} data-testid={`src-row-${p.slug}`}
                     style={{
                       display: "flex", alignItems: "center", justifyContent: "space-between",
                       padding: "8px 12px", background: "#080c12",
                       borderRadius: 8, border: "1px solid #1e2530",
                     }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Icon size={14}
                      color={hit ? "#ef4444" : (checking ? "#64748b" : "#10b981")}
                      className={checking ? "animate-spin" : ""} />
                    <span style={{ color: "#f1f5f9", fontSize: 12 }}>{p.name}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {hit && <span style={{ color: "#ef4444", fontSize: 11, fontWeight: 700 }}>
                      {p.matches} hit{p.matches !== 1 && "s"}
                    </span>}
                    <span style={{
                      padding: "1px 8px", borderRadius: 999, background: b.bg,
                      border: `1px solid ${b.border}`, color: b.fg,
                      fontSize: 9, fontWeight: 800, letterSpacing: 0.7,
                    }}>{b.txt}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Risk gauge + factor breakdown */}
      {report && (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4" style={{ marginBottom: 20 }}>
          <div className="col-span-1 md:col-span-5" style={cardStyle}>
            <h3 style={cardHeader}>Risk Score</h3>
            <div style={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
              <RiskGauge score={report.risk.score} band={report.risk.band}
                          color={report.risk.color} />
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: "#64748b", textAlign: "center" }}>
              Raw: {report.risk.raw_sum} · capped at {report.risk.max_possible}
            </div>
          </div>
          <div className="col-span-1 md:col-span-7" style={cardStyle}>
            <h3 style={cardHeader}>Factor Breakdown</h3>
            {report.risk.factors.length === 0 ? (
              <div style={{ padding: 16, color: "#64748b", fontSize: 13 }}>
                No risk factors fired — subject is clear across all sources.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {report.risk.factors.map((f, i) => (
                  <div key={i} data-testid={`factor-${f.code}`} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "10px 14px", background: "#080c12",
                    borderRadius: 8, border: "1px solid #1e2530",
                  }}>
                    <div>
                      <div style={{ color: "#f1f5f9", fontSize: 13, fontWeight: 600 }}>
                        {f.label}
                      </div>
                      <div style={{ color: "#64748b", fontSize: 10 }}>{f.source}</div>
                    </div>
                    <div style={{
                      color: "#ef4444", fontWeight: 800, fontSize: 18,
                      fontFamily: "ui-monospace, monospace",
                    }}>+{f.points}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Watchlist matches */}
      {report && (
        <div style={{ ...cardStyle, marginBottom: 20 }}>
          <h3 style={cardHeader}>Watch-list Matches</h3>
          {/* Live OpenSanctions */}
          {(report.sanctions_pep.matches || []).length > 0 && (
            <div style={{ marginBottom: 12 }}>
              {report.sanctions_pep.matches.map((m, i) => (
                <MatchCard key={`os-${i}`}
                  source="OpenSanctions"
                  lastRefreshed={report.source_status.find(s => s.slug === "opensanctions")?.last_refreshed}
                  isSample={report.sanctions_pep.mode !== "live"}
                  name={m.caption}
                  meta={`Score ${(m.score * 100 | 0)} · ${(m.datasets || []).slice(0, 3).join(", ")}`}
                  topics={m.topics} />
              ))}
            </div>
          )}
          {/* Indian sources */}
          {(report.indian_hits || []).map((h, i) => (
            <MatchCard key={`i-${i}`}
              source={h.source_name}
              lastRefreshed={h.source_last_refreshed}
              isSample={h.is_sample}
              name={h.matched_name}
              meta={h.matched_pan ? `PAN ${h.matched_pan}` : (h.matched_cin ? `CIN ${h.matched_cin}` : "")}
              topics={[h.match_label]} />
          ))}
          {(report.sanctions_pep.matches || []).length === 0 &&
           (report.indian_hits || []).length === 0 && (
            <div style={{ padding: 16, color: "#64748b", fontSize: 13 }}>
              No matches across any source.
            </div>
          )}
        </div>
      )}

      {/* Network graph */}
      {report && (
        <div style={{ ...cardStyle, marginBottom: 20 }}>
          <h3 style={cardHeader}>
            <Network size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
            Linked Entities — click a node to re-screen
          </h3>
          <NetworkGraph network={report.network} onNodeClick={onNodeClick} />
        </div>
      )}

      {/* STR narrative */}
      {report && (
        <div style={{ ...cardStyle, marginBottom: 60 }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
            <h3 style={{ ...cardHeader, marginBottom: 0 }}>
              <FileText size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
              FIU-IND STR Narrative
            </h3>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={generateNarrative} disabled={narrativeLoading}
                       data-testid="generate-narrative-btn"
                       style={btnPrimary}>
                {narrativeLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {narrative ? "Regenerate" : "Generate"}
              </button>
              <button onClick={downloadPdf} disabled={!narrative}
                       data-testid="download-pdf-btn"
                       style={{ ...btnPrimary, background: narrative ? "#10b981" : "#334155",
                                  cursor: narrative ? "pointer" : "not-allowed" }}>
                <FileDown size={12} /> Export PDF
              </button>
            </div>
          </div>
          <p style={{ color: "#64748b", fontSize: 11, margin: "0 0 10px" }}>
            Only real (LIVE) hits are sent to the LLM. Sample rows are excluded automatically.
          </p>
          <textarea data-testid="narrative-textarea"
            value={narrative}
            onChange={e => setNarrative(e.target.value)}
            placeholder="Click 'Generate' to draft a narrative. The text is editable before export."
            rows={14}
            style={{
              width: "100%", padding: 14, background: "#080c12",
              border: "1px solid #1e2530", borderRadius: 10,
              color: "#cbd5e1", fontSize: 12, fontFamily: "ui-monospace, monospace",
              lineHeight: 1.6, resize: "vertical",
            }} />
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------

const inputStyle = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  background: "#080c12", border: "1px solid #1e2530", color: "#f1f5f9",
  fontSize: 13, marginTop: 4, fontFamily: "ui-monospace, monospace",
};

const cardStyle = {
  background: "#0d1117", border: "1px solid #1e2530", borderRadius: 14, padding: 20,
};

const cardHeader = {
  color: "#f1f5f9", fontSize: 13, fontWeight: 700, margin: 0, marginBottom: 12,
  letterSpacing: 0.3,
};

const btnPrimary = {
  padding: "7px 14px", borderRadius: 8, background: "#2563eb", color: "#fff",
  border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 6,
};

function MatchCard({ source, lastRefreshed, isSample, name, meta, topics }) {
  return (
    <div data-testid={`match-card-${(source || "x").toLowerCase().replace(/\s+/g, "-")}`}
         style={{
      padding: 14, background: "#080c12", borderRadius: 10,
      border: `1px solid ${isSample ? "#f59e0b33" : "#ef444433"}`,
      marginBottom: 8,
    }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Database size={12} color={isSample ? "#f59e0b" : "#ef4444"} />
          <span style={{ color: "#f1f5f9", fontWeight: 700, fontSize: 12 }}>{source}</span>
          <span style={{ color: "#64748b", fontSize: 10 }}>
            updated {lastRefreshed ? new Date(lastRefreshed).toLocaleDateString() : "—"}
          </span>
        </div>
        {isSample ? (
          <span style={{
            padding: "2px 8px", borderRadius: 999,
            background: "rgba(245,158,11,0.15)", color: "#f59e0b",
            border: "1px solid #f59e0b66", fontSize: 9, fontWeight: 800,
            letterSpacing: 0.6,
          }} title="Sample row from a CSV-uploadable table — verify before using">
            SAMPLE DATA — not a real match
          </span>
        ) : (
          <span style={{
            padding: "2px 8px", borderRadius: 999,
            background: "rgba(239,68,68,0.15)", color: "#ef4444",
            border: "1px solid #ef444466", fontSize: 9, fontWeight: 800,
            letterSpacing: 0.6,
          }}>LIVE MATCH</span>
        )}
      </div>
      <div style={{ color: "#f1f5f9", fontSize: 14, fontWeight: 600 }}>{name}</div>
      {meta && <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 2 }}>{meta}</div>}
      {(topics || []).length > 0 && (
        <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
          {topics.filter(Boolean).map((t, i) => (
            <span key={i} style={{
              padding: "1px 6px", borderRadius: 4,
              background: "#1e2530", color: "#94a3b8", fontSize: 9,
              fontFamily: "ui-monospace, monospace",
            }}>{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}
